import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import SurveyStar from './surveyStars';
import ContactForm from './contactFormRevamp';
import ContactFormLandScape from './contactFormRevampLandScape';
import find from 'lodash/find';
import pickBy from 'lodash/pickBy';
import { LABEL } from '../../veriables';
import cn from 'classnames';
import { connect } from 'react-redux';
import styles from './survey.module.scss';
import { flowType } from '@utility/userInfo';
import { ProgressBar } from './ProgressBar';
import welcomeImageUrl from '@assets/images/survey/welcomeImage.svg';
import ThankYouImg from '@assets/images/survey/ThankYouImg.svg';
import { TouchableButton, TouchableDiv } from '@components/common/Touchable';
import { bindActionCreators } from 'redux';
import { setErrorPopUp } from '@reducers/notification';

const HEIGHT_CONFIG = (isLandScape) => ({
  0: { question: 8, itemList: 91 },
  3: { question: 8, itemList: isLandScape ? 77 : 91 },
  2: { question: 14, itemList: 85 },
  5: { question: 14, itemList: 85 },
  1: { question: 12, itemList: 87 },
  4: { question: 12, itemList: isLandScape ? 85 : 87 },
  7: { question: 12, itemList: 87 },
  6: { question: 22, itemList: 77 },
  default: { question: 18, itemList: 79 }
});

const SurveyComponent = memo((props) => {
  const { surveyData, serverAppTheme, isLandScape } = props;

  const flowTypeValue = useMemo(() => flowType(), []);
  const isKiosk = flowTypeValue === 'kiosk';
  const isServer = flowTypeValue === 'serverApp';

  const [allSelectedValue, setAllSelected] = useState([]);
  const [onQuestion, setOnQuestion] = useState(surveyData.welcomeMessage ? 'none' : 0);
  const [lastQuestion, setLastQuestion] = useState(false);
  const [surveyQuestion, setSurveyQuestion] = useState([]);
  const [progressWidth, setProgressWidth] = useState(0);

  const dynamicHeight = useMemo(() => {
    const heightConfig =  HEIGHT_CONFIG(isLandScape);
    return heightConfig[onQuestion] || heightConfig.default;
  }, [onQuestion, isLandScape]);

  const enableContactForm = useMemo(() => {
    return surveyData.enableContactMe &&
      onQuestion === surveyQuestion.length &&
      !lastQuestion;
  }, [surveyData.enableContactMe, onQuestion, surveyQuestion.length, lastQuestion]);

  const selected = useMemo(() => {
    if (surveyQuestion.length === 0) return [];

    return surveyQuestion.map((question) => ({
      ...question,
      isAlreadySelected: allSelectedValue.some(
        (menuItem) => menuItem.questionId === question.surveyQuestionId
      )
    }));
  }, [allSelectedValue, surveyQuestion]);

  const addOrRemove = useCallback((array, item) => {
    const existingIndex = array.findIndex((a) => a.questionId === item.questionId);

    if (existingIndex !== -1) {
      const newArray = [...array];
      const existing = newArray[existingIndex];

      if (array.some((e) => e.responseId === item.responseId)) {
        if (!item.note) {
          newArray.splice(existingIndex, 1);
        } else {
          newArray[existingIndex] = { ...existing, note: item.note };
        }
      } else {
        newArray[existingIndex] = {
          ...existing,
          responseId: item.responseId || null
        };
      }
      return newArray;
    }

    return [...array, item];
  }, []);

  const selectedOption = useCallback((options, value) => {
    const prevArrayLength = allSelectedValue.length;
    const selectedAnswer = {
      questionId: options.surveyQuestionId,
      responseId: options.id,
      note: value,
    };

    const cleanedObject = pickBy(selectedAnswer, (v) => v !== undefined);
    const selectedButtons = addOrRemove(allSelectedValue, cleanedObject);

    setAllSelected(selectedButtons);
    sessionStorage.setItem('selectedQuestions', JSON.stringify(selectedButtons));

    if (!value && selectedButtons.length >= prevArrayLength) {
      const questionLength = surveyData.enableContactMe ?
        surveyQuestion.length :
        surveyQuestion.length - 1;

      setTimeout(() => {
        if (onQuestion < questionLength) {
          setOnQuestion(onQuestion + 1);
          if (surveyQuestion.length - 1 !== onQuestion) {
            setProgressWidth(prev => prev + 100 / surveyQuestion.length);
          }
        } else {
          setLastQuestion(true);
        }
      }, 600);
    }
  }, [allSelectedValue, addOrRemove, onQuestion, surveyData.enableContactMe, surveyQuestion.length]);

  const onDone = useCallback(() => {
    if (surveyData.questions.length === 0) return;

    let unansweredQuestions = [];

    if (allSelectedValue.length === 0) {
      unansweredQuestions = surveyData.questions.map((data) => ({
        questionId: data.surveyQuestionId,
        responseId: null
      }));
    } else {
      unansweredQuestions = surveyData.questions
        .filter(data => !allSelectedValue.some(
          selectedQuest => selectedQuest.questionId === data.surveyQuestionId
        ))
        .map(data => ({
          questionId: data.surveyQuestionId,
          responseId: null
        }));
    }

    const finalAnswers = [...allSelectedValue, ...unansweredQuestions];
    setAllSelected(finalAnswers);
    props.basicSurveyResult(finalAnswers, false);
  }, [allSelectedValue, surveyData.questions, props]);

  const onSkipSurvey = useCallback(() => {
    if (surveyData.questions.length === 0) return;

    const skippedAnswers = surveyData.questions.map((data) => ({
      questionId: data.surveyQuestionId,
      responseId: null
    }));

    props.basicSurveyResult(skippedAnswers, true);
  }, [surveyData.questions, props]);

  const submitContactForm = useCallback(() => {
    if (surveyData.confirmationMessage === '') {
      onDone();
    } else {
      setLastQuestion(true);
    }
  }, [surveyData.confirmationMessage, onDone]);

  const handleBackButton = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (surveyData.confirmationMessage !== '' && lastQuestion) {
      setLastQuestion(false);
      return;
    }

    if (lastQuestion) {
      setLastQuestion(false);
    } else if (onQuestion === 0) {
      setOnQuestion('none');
      setProgressWidth(0);
    } else {
      const newProgress = Math.max(0, progressWidth - 100 / surveyQuestion.length);
      setProgressWidth(newProgress);
      setTimeout(() => setOnQuestion(onQuestion - 1), 10);
    }
  }, [surveyData.confirmationMessage, lastQuestion, onQuestion, progressWidth, surveyQuestion.length]);

  const handleNextButton = useCallback(() => {
    setOnQuestion(onQuestion + 1);
    if (surveyQuestion.length - 1 !== onQuestion) {
      setProgressWidth(prev => prev + 100 / surveyQuestion.length);
    }
  }, [onQuestion, surveyQuestion.length]);

  const handleContinueButton = useCallback((event) => {
    event.preventDefault();
    setOnQuestion(0);
    setProgressWidth(100 / surveyQuestion.length);
  }, [surveyQuestion.length]);

  const handleDoneButton = useCallback(() => {
    if (lastQuestion) {
      onDone();
    } else {
      setLastQuestion(true);
      setProgressWidth(prev => prev + 100 / surveyQuestion.length);
    }
  }, [lastQuestion, onDone, surveyQuestion.length]);

  useEffect(() => {
    const selectedAnswers = JSON.parse(sessionStorage.getItem('selectedQuestions') || '[]');
    if (selectedAnswers.length > 0) {
      setAllSelected(selectedAnswers);
    }
  }, []);

  useEffect(() => {
    if (surveyData.questions?.length > 0) {
      const processedQuestions = surveyData.questions.map((question) => ({
        ...question,
        responses: question.type === 4 ?
          [...question.responses].reverse() :
          question.responses
      }));
      setSurveyQuestion(processedQuestions);
    }
  }, [surveyData.questions]);

  const questionLength = useMemo(() =>
    surveyData.enableContactMe ? surveyQuestion.length : surveyQuestion.length - 1,
    [surveyData.enableContactMe, surveyQuestion.length]
  );

  const isLastQuestion = useMemo(() =>
    onQuestion === questionLength,
    [onQuestion, questionLength]
  );

  return (
    <div
      className='h-100'
      style={{
        background: serverAppTheme?.background_color,
        color: serverAppTheme?.primary_text
      }}
    >
      {onQuestion !== 'none' && !enableContactForm && !lastQuestion && (
        <ProgressBar
          progressWidth={progressWidth}
          onQuestion={onQuestion}
          surveyData={surveyData}
          surveyQuestion={surveyQuestion}
          enableContactForm={enableContactForm}
          serverAppTheme={serverAppTheme}
          isLandScape={isLandScape}
        />
      )}

      <div
        className={cn("col-12 m-0 w-100 fs_16 px-0", !isLandScape && "pt-3")}
        style={{
          top: !isLandScape && '2%',
          height: lastQuestion ? '100%' : !isLandScape && '88%'
        }}
      >
        <div className={cn(
          'p-0',
          (!isLandScape || lastQuestion)
            ? (props.showCheckSeat() ? 'h-84' : 'h-90')
            : '',
          surveyData && (lastQuestion || onQuestion === 'none') &&
          'col-12 px-0 justify-content-center align-items-center',
          !(welcomeImageUrl || surveyData.welcomeImageUrl) && 'd-flex',
          (surveyData.welcomeMessage || surveyData.confirmationMessage) ? "" : 'overflowY'
        )}>

          {enableContactForm &&
            React.createElement(
              isLandScape ? ContactFormLandScape : ContactForm,
              {
                submitContactForm,
                confirmationMessage: surveyData.confirmationMessage,
                isServer,
                isKiosk,
                setOnQuestion,
                onQuestion,
                setProgressWidth,
                progressWidth,
                surveyQuestion,
                serverAppTheme,
                setErrorPopUp: props.setErrorPopUp,
                isLandScape
              }
            )
          }

          {!props.isHeight && surveyData && !lastQuestion && onQuestion === 'none' && (
            <>
              <div className={cn('row mx-0 align-items-center', isKiosk && 'h-90')}
                style={{ marginBottom: isKiosk ? '' : '1.5vh' }}>
                <img
                  src={surveyData.welcomeImageUrl || welcomeImageUrl}
                  alt=""
                  className={cn('img-fluid mx-auto d-block', isKiosk && styles.fitImage)}
                  style={isKiosk ? {} : { height: '55vh' }}
                />
              </div>
              <div className={cn(
                'col-12 p-2 row mx-0 fs_26', isLandScape ? 'h-25 text-center' : 'h-30 d-flex justify-content-center align-items-center',
                isKiosk && 'h-10'
              )} style={{
                fontSize: isKiosk ? '60px' : isLandScape ? '24px' : '30px',
                fontWeight: "600"
                }}>
                {surveyData.welcomeMessage}
              </div>
            </>
          )}

          {!props.isHeight && surveyData && lastQuestion && onQuestion !== 'none' && (
            <>
              <div className={cn('row d-flex justify-content-center mx-0 align-items-center', isKiosk && 'h-90')}
                style={{ marginBottom: '2vh' }}>
                <img
                  src={surveyData.confirmationImageUrl || ThankYouImg}
                  alt=""
                  className={cn('img-fluid d-block', isKiosk && styles.fitImage)}
                  style={{ height: '55vh' }}
                />
              </div>
              <div className={cn(
                'col-12 d-flex justify-content-center p-2 row mx-0 align-items-center h-30', !isLandScape ? 'fs_28' : '',
                isKiosk && 'h-10'
              )} style={{
                fontSize: isKiosk ? '60px' : (isLandScape ? '24px' : '30px'),
                fontWeight: "bold",
                marginBottom: !isLandScape ? '2vh' : '',
                overflow: "scroll",
              }}>
                {surveyData.confirmationMessage}
              </div>
            </>
          )}

          {surveyData?.questions && !lastQuestion && surveyQuestion.map((question, i) => {
            if (onQuestion !== i) return null;
            return (
              <div
                key={question.id}
                className={cn(
                  'row mx-0 align-content-start w-100 overflowY',
                  isKiosk ? 'h-90' : (isLandScape ? (props.showCheckSeat() ? 'h73vh' : 'h78vh') : 'h-100')
                )}
              >
                <div
                  className={cn(
                    styles.fixedQuestionHeader,
                    'col-12 position-relative p-1', isLandScape ? styles.fixedQuestionHeaderMargin : 'overflowY'
                  )}
                  style={{ fontSize: isKiosk ? '60px' : '24px' }}
                >
                  {question.name}
                </div>

                <div
                  className={cn(
                    isLandScape
                      ? question.type === 9
                        ? `p-1 text-center col-12`
                        : question.type === 4
                          ? `p-1 m-auto text-center col-11 ${styles.topMargin}`
                          : `p-1 m-auto text-center col-6`
                      : `p-1 m-auto text-center col-12`,
                    question.type === 3 && 'py-3',
                    (question.type === 2 || question.type === 1) &&
                    'row justify-content-center align-items-center'
                  )}
                  style={{
                    height: `${dynamicHeight.itemList}%`,
                    overflowY: (!isLandScape || question.type !== 4) ? 'scroll' : 'unset',
                    borderRadius: '5px',
                    display: question.type === 1 ? 'flex' : '',
                    alignItems: 'center',
                    paddingTop: question.type === 2 ? '50%' : ''
                  }}
                >
                  <div className={cn(
                    question.type === 1 && 'p-3 rounded-pill',
                    question.type === 2 && 'col-12', isLandScape && 'px-0',
                    question.type === 4 ? isLandScape ? 'd-flex flex-direction-row col-12 px-0' : 'col-12 px-0' : '',
                    question.type === 9 && 'col-12 p-0 h-100'
                  )}>
                    {question.responses
                      .sort((a, b) => question.type === 4 ? b.id - a.id : a.id - b.id)
                      .map((option, index) => {
                        let isSelected = false;
                        let note = '';

                        if (question.type === 1) {
                          const selectedValue = allSelectedValue.find(
                            e => e.questionId === option.surveyQuestionId && e.responseId
                          );
                          isSelected = selectedValue ?
                            option.id <= selectedValue.responseId : false;
                        } else {
                          isSelected = allSelectedValue.some(
                            a => a.questionId === option.surveyQuestionId &&
                              a.responseId === option.id
                          );
                        }

                        if (question.type === 9) {
                          const selectedItem = find(
                            allSelectedValue,
                            a => a.questionId === option.surveyQuestionId &&
                              a.responseId === option.id
                          );
                          note = selectedItem?.note || '';
                        }

                        return (
                          <SurveyStar
                            key={option.id}
                            options={option}
                            questionType={question.type}
                            selectedOption={selectedOption}
                            index={index}
                            lastIndex={question.responses.length - 1}
                            responses={question.responses}
                            objectType={question.questionTarget}
                            isSelected={isSelected}
                            allSelectedValue={allSelectedValue}
                            isRequired={question.isRequired}
                            note={note}
                            serverAppTheme={serverAppTheme}
                            isLandScape={isLandScape}
                          />
                        );
                      })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {onQuestion !== 'none' ? (
          !enableContactForm && (
            <div className={cn(
              'h-12 row col-12 mx-0 p-0 justify-content-center',
              isKiosk ? 'align-items-end' : !isLandScape && 'pb-3 align-items-center'
            )} style={isKiosk ? {
              position: 'fixed',
              bottom: '10%',
              width: '100%'
            } : isLandScape && isLastQuestion ? { position: 'relative' , bottom: '9px'} : {}}>
              <div className={cn(
                'col-6 text-center pl-0 py-1 h-100', isLandScape ? 'pr-2' : ' pr-1',
                (onQuestion === 0 || (surveyData.welcomeMessage === '' && onQuestion === 0)) && 'd-none'
              )}>
                <TouchableButton
                  className={cn(
                    styles.secondaryBtn,
                    isLandScape ? styles.questionsbtnHeightLandScape : styles.btnHeight,
                    'rounded w-100 d-flex justify-content-center align-items-center od-cancel-btn-bg-color  od-cancel-btn-font-color od-cancel-btn-st-color',
                    isKiosk ? 'btn35' : 'btn90'
                  )}
                  style={{
                    background: serverAppTheme?.OFF_Premise_Btn,
                    color: serverAppTheme?.cancleBtn_font
                  }}
                  onClick={handleBackButton}
                  rippleEffect={true}
                  role="presentation"
                >
                  <div className={isKiosk ? 'fs-30' : styles.btnStyle}>
                    {LABEL().BACK}
                  </div>
                </TouchableButton>
              </div>

              <div className={cn(
                (onQuestion === 0 || (surveyData.welcomeMessage === '' && onQuestion === 0)) ?
                  isLandScape ? 'col-7' : 'col-12' : 'col-6',
                "text-center px-0 pt-1 h-100"
              )}>
                {!isLastQuestion && (
                  <TouchableButton
                    type="button"
                    disabled={
                      !(selected[onQuestion]?.isAlreadySelected) &&
                      selected[onQuestion]?.isRequired
                    }
                    className={cn(
                      styles.skipBtn,
                      isLandScape ? styles.questionsbtnHeightLandScape : styles.btnHeight,
                      'rounded btn-ripple w-100 d-flex justify-content-center align-items-center od-primary-btn-bg-color od-primary-btn-font-color',
                      isKiosk ? 'btn35' : 'btn90'
                    )}
                    onClick={(e) => { e.preventDefault(); handleNextButton()}}
                    style={{ backgroundColor: '#2541A4', color: 'white' }}
                    rippleEffect={true}
                  >
                    <div className={cn(isKiosk ? 'fs-30' : styles.btnStyle)}>
                      {LABEL().NEXT}
                    </div>
                  </TouchableButton>
                )}

                {isLastQuestion && surveyData.confirmationMessage !== '' && (
                  <TouchableButton
                    type="button"
                    disabled={
                      !(selected[onQuestion]?.isAlreadySelected) &&
                      selected[onQuestion]?.isRequired
                    }
                    className={cn(
                      styles.skipBtn,
                      isLandScape ? styles.questionsbtnHeightLandScape : styles.btnHeight,
                      'rounded btn-ripple w-100 d-flex justify-content-center align-items-center od-primary-btn-bg-color od-primary-btn-font-color',
                      isKiosk ? 'btn35' : 'btn90'
                    )}
                    onClick={handleDoneButton}
                    style={{ backgroundColor: '#2541A4', color: 'white' }}
                    rippleEffect={true}
                  >
                    <div className={cn(isKiosk ? 'fs-30' : styles.btnFontsize)}>
                      {lastQuestion ? LABEL().DONE : LABEL().NEXT}
                    </div>
                  </TouchableButton>
                )}

                {isLastQuestion && surveyData.confirmationMessage === '' && (
                  <TouchableButton
                    type="button"
                    disabled={
                      !(selected[onQuestion]?.isAlreadySelected) &&
                      selected[onQuestion]?.isRequired
                    }
                    className={cn(
                      styles.skipBtn,
                      styles.btnHeight,
                      'rounded btn-ripple w-100 d-flex justify-content-center align-items-center od-primary-btn-bg-color od-primary-btn-font-color',
                      isKiosk ? 'btn35' : 'btn90'
                    )}
                    onClick={() => {
                      onDone();
                      setProgressWidth(100);
                    }}
                    style={{ backgroundColor: '#2541A4', color: 'white' }}
                    rippleEffect={true}
                  >
                    <div className={cn(isKiosk ? 'fs-30' : styles.btnFontsize)}>
                      {LABEL().DONE}
                    </div>
                  </TouchableButton>
                )}
              </div>
            </div>
          )
        ) : (
          <div className={cn('row mx-0 align-items-end h-12', isLandScape && "d-flex justify-content-center align-items-center")}
            style={isKiosk ? {
              position: 'fixed',
              bottom: '10%',
              width: '100%',
              left: '4%'
            } : {
              position: 'fixed',
              bottom: !isLandScape && '1%',
              width: '93%',
              left: '3.5%'
            }}>
            <TouchableDiv
              className={cn('col-12 p-0 fs-20', isLandScape ? (props.showCheckSeat() ? 'text-center' : `text-center ${styles.surveyTop}`) : 'text-right')}
              onClick={onSkipSurvey}
              role="presentation"
            >
              <div
                className={cn(isKiosk ? 'fs-30' : 'fs_16')}
                style={{
                  textDecorationLine: "underline",
                  fontWeight: '600',
                  color: serverAppTheme?.link_color
                }}
              >
                {LABEL().SKIP_SURVEY}
              </div>
            </TouchableDiv>

            <div className={cn(
              (surveyData && !lastQuestion && onQuestion === 'none') ?
                'text-center p-0 py-1 h-75' : 'd-none', isLandScape ? (props.showCheckSeat() ? `col-6 ${styles.topPaddingShowCheck}` : `col-6 ${styles.toppadding}`) : 'col-12'
            )}>
              {surveyData && !lastQuestion && onQuestion === 'none' && (
                <TouchableButton
                  type="button"
                  className={cn(
                    isLandScape ? styles.btnHeightLandScape : styles.btnHeight,
                    'my-1 btn-ripple d-flex justify-content-center align-items-center text-white od-primary-btn-bg-color od-primary-btn-font-color',
                    isKiosk ? 'w-70 btn35' : 'w-100 btn90'
                  )}
                  onClick={handleContinueButton}
                  style={{ backgroundColor: '#2541A4', color: '#fff' }}
                  rippleEffect={true}
                >
                  <div className={cn(isKiosk ? 'fs-30' : 'fs_24 fw-600')}>
                    {isKiosk ? LABEL().KIOSK_CONTINUE : LABEL().PC_CONTINUE}
                  </div>
                </TouchableButton>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

SurveyComponent.displayName = 'SurveyComponent';

const mapStateToProps = ({ theme }) => ({
  serverAppTheme: theme.serverAppTheme,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  setErrorPopUp,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SurveyComponent);